import type { Option } from "@tento-lona/sheets";
import { Network } from "../network.ts";
// `AuthId` / `CalendarId` are the branded ids of `@tento/lona/sheets`
// (`../ids`): an auth id is not accepted where a calendar id is expected.
// Consumers import them from `@tento/lona/sheets`, their one import path.
import { AuthId, CalendarId } from "@tento-lona/sheets";

/**
 * A calendar's display record paired with the id of the account it belongs
 * to — what `client.calendars.peek` / `peekPrimary()` hand out.
 */
export type AuthScopedCalendarDisplay = [AuthId, Network.CalendarDisplay];

/**
 * A calendar addressed by its account and its id: `[authId, calendarId]`,
 * both branded, so the pair cannot be written backwards. The shape a
 * calendar-list alias persists in its `customCalendars` attribute (a JSON
 * pair of strings).
 */
export type AuthScopedCalendarId = [authId: AuthId, calendarId: CalendarId];

/** Values that go with `AuthScopedCalendarId`: `of`, `ofEvent`, `parse`, `key`, `equals`. */
export namespace AuthScopedCalendarId {
  /** The address of a held calendar display. */
  export function of(display: AuthScopedCalendarDisplay): AuthScopedCalendarId {
    return [display[0], CalendarId.of(display[1].id)];
  }

  /**
   * The calendar an event (`EventItem.inner`, a wire `Network.Event`) belongs
   * to, read by field name. Throws on an empty id (an event always has both).
   */
  export function ofEvent(
    event: { readonly authId: AuthId; readonly calendarId: CalendarId },
  ): AuthScopedCalendarId {
    return [event.authId, event.calendarId];
  }

  /** The address a persisted `[authId, calendarId]` pair names, or `null` when either id is empty. */
  export function parse(authId: string, calendarId: string): Option<AuthScopedCalendarId> {
    const auth = AuthId.parse(authId);
    const calendar = CalendarId.parse(calendarId);
    return auth == null || calendar == null ? null : [auth, calendar];
  }

  /** The address as one string key (`authId:calendarId`). */
  export function key(acid: AuthScopedCalendarId): string {
    return `${acid[0]}:${acid[1]}`;
  }

  /** Whether two addresses name the same calendar. */
  export function equals(a: AuthScopedCalendarId, b: AuthScopedCalendarId): boolean {
    return a[0] === b[0] && a[1] === b[1];
  }
}

/**
 * The calendar an event write lands in (`client.events.create`, `moveTo`,
 * `copyTo`, `rowForCalendar`): named ids —
 * `{ authId, calendarId }`, e.g. a `WritableCalendar` — or the held display
 * (`AuthScopedCalendarDisplay`, e.g. `client.calendars.peekPrimary()`). Both
 * name their parts, so neither can be passed backwards.
 */
export type CalendarTarget = CalendarTarget.Ids | AuthScopedCalendarDisplay;

/** Types and values that go with `CalendarTarget`: `Ids`, `ids`, `of`. */
export namespace CalendarTarget {
  /** A calendar by its named, branded ids. */
  export type Ids = { readonly authId: AuthId; readonly calendarId: CalendarId };

  /**
   * The named ids of `target`. A bare `[authId, calendarId]` string pair from
   * an untyped caller (the retired form) throws a `TypeError` naming the fix.
   */
  export function ids(target: CalendarTarget): Ids {
    if (Array.isArray(target)) {
      const display: unknown = target[1];
      if (typeof display !== "object" || display === null) {
        throw new TypeError(
          "CalendarTarget: pass { authId, calendarId } or a held calendar display " +
            "([authId, CalendarDisplay]), not an [authId, calendarId] string pair",
        );
      }
      return { authId: target[0], calendarId: CalendarId.of(target[1].id) };
    }
    return target;
  }

  /** The named ids of a calendar address. */
  export function of(acid: AuthScopedCalendarId): Ids {
    return { authId: acid[0], calendarId: acid[1] };
  }
}

/**
 * Minimal interface for calendar lookup, used by EventItem methods.
 * A host's calendar store satisfies it structurally.
 */
export interface CalendarLookup {
  peek(acid: AuthScopedCalendarId): Option<AuthScopedCalendarDisplay>;
}

/** Whether a calendar accepts writes. Server-computed capabilities win
 *  when present; absent capabilities (older server) fall back to
 *  access-role editability — NEVER to read-only. */
export function calendarWritable(cald: Network.CalendarDisplay): boolean {
  if (cald.capabilities) return cald.capabilities.writable;
  return cald.accessRole === "owner" || cald.accessRole === "writer";
}

/** Whether a cross-calendar move can use the provider's native
 *  (id-preserving) move API: BOTH ends must advertise it. Absent
 *  capability data falls back to allowed (the pre-capability
 *  behavior). Callers without native move fall back to copy+delete. */
export function canNativeMove(
  source: Option<Network.CalendarDisplay>,
  target: Network.CalendarDisplay,
): boolean {
  const nativeOk = (c: Option<Network.CalendarDisplay>) =>
    !c || !c.capabilities || c.capabilities.nativeMove;
  return nativeOk(source) && nativeOk(target);
}
